import React, { useMemo } from "react";
import "../assets/css/WeatherSidebar.css";
import Windy from "../assets/images/Windy.png";
import Sunny from "../assets/images/Sunny.svg";
import Cloudy from "../assets/images/Cloudy.svg";
import Rainy from "../assets/images/Rainy.svg";
import Stormy from "../assets/images/Stormy.svg";
import Dropdown from "../Pages/Dropdown.js";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FiMapPin } from "react-icons/fi";

const WeatherSidebar = ({
  regionalStateData = [],
  zonesForState = [],
  woredasForZone = [],
  selectedRegionalState,
  selectedZone,
  selectedWoreda,
  selectedKebele,
  setSelectedRegionalState,
  setSelectedZone,
  setSelectedWoreda,
  setSelectedKebele,
  kebelForWoreda = []
}) => {
  const filteredZone = useMemo(() => {
    return zonesForState
      ?.filter((z) => z?.regionalState === selectedRegionalState)
      ?.map((z) => z?.zone).sort() || [];
  }, [zonesForState, selectedRegionalState]);

  const filteredWoreda = useMemo(() => {
    return woredasForZone
      ?.filter((w) => w?.zone === selectedZone)
      ?.map((w) => w?.woreda).sort() || [];
  }, [woredasForZone, selectedZone]);

  const filteredKebele = useMemo(() => {
    return kebelForWoreda
      ?.filter((w) => (w?.woreda === selectedWoreda))
      ?.map((w) => w?.kebele).sort() || [];
  }, [kebelForWoreda, selectedWoreda]);
  const handleRegionalChange = (e) => {
    const region = e?.target?.value ?? "";
    setSelectedRegionalState(region);
    setSelectedZone("");
    setSelectedWoreda("");
    setSelectedKebele("");
  };

  const handleZoneChange = (e) => {
    const zone = e?.target?.value ?? "";
    setSelectedZone(zone);
    setSelectedWoreda("");
    setSelectedKebele("");
  };

  const handleWoredaChange = (e) => {
    const woreda = e?.target?.value ?? "";
    setSelectedWoreda(woreda);
    setSelectedKebele("");
  };

  const handleKebeleChange = (e) => {
    const kebele = e?.target?.value ?? "";
    setSelectedKebele(kebele);
  };

  const getWeatherIcon = (cityData) => {
    const temp = cityData?.parameters?.weather?.temperature_C;

    if (temp == null) return Sunny;
    if (temp >= 30) return Sunny;
    if (temp >= 23) return Cloudy;
    if (temp >= 16) return Windy;
    if (temp >= 10) return Rainy;
    return Stormy;
  };

  const previewCityName = (() => {
    if (selectedKebele) return selectedKebele;
    if (selectedWoreda) return selectedWoreda;
    if (selectedZone) return selectedZone;
    if (selectedRegionalState) return selectedRegionalState;
    return "Select Location";
  })();

  const previewCityData = (() => {
    if (selectedKebele) {
      const kebeleObj = woredasForZone.flatMap((w) => w.kebeles || [])
        .find((k) => k.kebele === selectedKebele);
      if (kebeleObj) return kebeleObj;
    }
    if (selectedWoreda) {
      const woredaObj = woredasForZone.find((w) => w.woreda === selectedWoreda);
      if (woredaObj) return woredaObj;
    }
    if (selectedZone) {
      const zoneObj = zonesForState.find((z) => z.zone === selectedZone);
      if (zoneObj) return zoneObj;
    }
    if (selectedRegionalState) {
      const stateObj = regionalStateData.find((s) => s.regionalState === selectedRegionalState);
      if (stateObj) return stateObj;
    }
    return null;
  })();

  const weatherIcon = getWeatherIcon(previewCityData);

  const getCondition = (city) => {
    const temp = city?.parameters?.weather?.temperature_C;
    if (temp == null) return "--";
    if (temp >= 30) return "Sunny";
    if (temp >= 23) return "Cloudy";
    if (temp >= 16) return "Windy";
    if (temp >= 10) return "Rainy";
    return "Stormy";
  };

  const condition = getCondition(previewCityData);
  const isZoneLoading = selectedRegionalState && zonesForState.length === 0;
  const isWoredaLoading = selectedZone && woredasForZone.length === 0;
  const isKebeleLoading = selectedWoreda && kebelForWoreda.length === 0;

  return (
    <div className="weather-sidebar-new">
      <div className="sidebar-container-new">
        <div className="sidebar-header-new">
          <div className="sidebar-header-content">
            <FiMapPin className="header-icon" />
            <h3 className="sidebar-title-new">Location Finder</h3>
          </div>
          <p className="sidebar-subtitle">Select your location to view weather data</p>

          <div className="sidebar-dropdown">
            <Dropdown
              label="Regional State"
              value={selectedRegionalState}
              options={regionalStateData?.map((item) => item.regionalState).sort() || []}
              onChange={handleRegionalChange}
              placeholder="Select Regional State"
            />
            {selectedRegionalState && (
              <AiOutlineCloseCircle
                onClick={() => handleRegionalChange({ target: { value: "" } })}
                style={{ cursor: "pointer", color: "black", fontSize: "1.1rem" }}
                title="Clear Regional State"
              />
            )}
          </div>
          <div className="sidebar-dropdown">
            {selectedRegionalState && (
              <>
                {isZoneLoading ? (
                  <p></p>
                ) : filteredZone?.length > 0 ? (
                  <>
                    <Dropdown
                      label="Zone"
                      value={selectedZone}
                      options={[...new Set(filteredZone)]}
                      onChange={handleZoneChange}
                      placeholder="Select Zone"
                    />
                    {selectedZone && (
                      <AiOutlineCloseCircle
                        onClick={() => handleZoneChange({ target: { value: "" } })}
                        style={{ cursor: "pointer", color: "black", fontSize: "1.1rem" }}
                      />
                    )}
                  </>
                ) : (
                  <p className="no-zones-message">No Zones available for this Regional State.</p>
                )}
              </>
            )}
          </div>
          <div className="sidebar-dropdown">
            {selectedZone && (
              <>
                {isWoredaLoading ? (
                  <p></p>
                ) : filteredWoreda?.length > 0 ? (
                  <>
                    <Dropdown
                      label="Woreda"
                      value={selectedWoreda}
                      options={[...new Set(filteredWoreda)]}
                      onChange={handleWoredaChange}
                      placeholder="Select Woreda"
                    />
                    {selectedWoreda && (
                      <AiOutlineCloseCircle
                        onClick={() => handleWoredaChange({ target: { value: "" } })}
                        style={{ cursor: "pointer", color: "black", fontSize: "1.1rem" }}
                        title="Clear Woreda"
                      />
                    )}
                  </>
                ) : (
                  <div className="no-zones-message">No woredas available for this Zone.</div>
                )}
              </>
            )}
          </div>
          <div className="sidebar-dropdown">
            {selectedWoreda && (
              <>
                {isKebeleLoading ? (
                  <p></p>
                ) : filteredKebele?.length > 0 ? (
                  <>
                    <Dropdown
                      label="Kebele"
                      value={selectedKebele}
                      options={[...new Set(filteredKebele)]}
                      onChange={handleKebeleChange}
                      placeholder="Select Kebele"
                    />
                    {selectedKebele && (
                      <AiOutlineCloseCircle
                        onClick={() => handleKebeleChange({ target: { value: "" } })}
                        style={{ cursor: "pointer", color: "black", fontSize: "1.1rem" }}
                        title="Clear Kebele"
                      />
                    )}
                  </>
                ) : (
                  <div className="no-zones-message">No kebeles available for this Woreda.</div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="weather-preview-card">
          <div className="preview-header">
            <p className="preview-title">Current Weather</p>
            <div className="preview-badge">{condition}</div>
          </div>

          <div className="preview-weather-icon">
            <img src={weatherIcon} alt="weather-icon" className="weather-icon-img" />
          </div>

          <div className="preview-details">
            <div className="preview-location">
              <FiMapPin className="location-pin" />
              <span>{previewCityName}</span>
            </div>
            <div className="preview-temp">
              <span className="preview-temp-value">
                {previewCityData?.parameters?.weather?.temperature_C ?? "--"}°
              </span>
              <span className="preview-temp-unit">C</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherSidebar;